import { BACKEND_URL } from "@/lib/api";
import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { compressImage } from "@/lib/compressImage";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Camera, X, Loader2 } from "lucide-react";

export const MAX_PHOTOS = 10;
export const photoUrl = (jobId, pid) => `${BACKEND_URL}/api/jobs/${jobId}/photos/${pid}`;

function PhotoGroup({ jobId, kind, photos, canAdd, canDelete, onAdd, onDelete, onOpen, busy, highlight }) {
  const ref = useRef(null);
  return (
    <div data-testid={`photos-${kind}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="label-caps text-muted-foreground">{kind === "before" ? "Before" : "After"} · {photos.length}</div>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {photos.map((p) => (
          <div key={p.id} className="relative group aspect-square">
            <button type="button" onClick={() => onOpen(p)} className="w-full h-full" data-testid={`photo-thumb-${p.id}`}>
              <img src={photoUrl(jobId, p.id)} alt={`${kind} photo`} loading="lazy" className="w-full h-full object-cover rounded-sm border border-border/60" />
            </button>
            {canDelete(p) && (
              <button type="button" onClick={() => onDelete(p)} data-testid={`photo-delete-${p.id}`}
                className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 opacity-80 hover:opacity-100">
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        ))}
        {canAdd && (
          <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid={`photo-add-${kind}`}
            className={`aspect-square flex flex-col items-center justify-center gap-1 rounded-sm border-2 border-dashed text-xs transition-colors ${highlight ? "border-brand text-brand bg-brand/5" : "border-border text-muted-foreground"} hover:border-brand hover:text-brand`}>
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
            Add {kind}
          </button>
        )}
      </div>
      <input ref={ref} type="file" accept="image/*" multiple className="hidden" data-testid={`photo-input-${kind}`}
        onChange={(e) => { onAdd(kind, Array.from(e.target.files || [])); e.target.value = ""; }} />
    </div>
  );
}

export default function JobPhotos({ job, user, highlight, onChange }) {
  const [photos, setPhotos] = useState(job?.photos || []);
  const [busy, setBusy] = useState("");
  const [open, setOpen] = useState(null);
  useEffect(() => { setPhotos(job?.photos || []); }, [job]);
  if (!job) return null;

  const isAdmin = user?.role === "admin";
  const canAdd = photos.length < MAX_PHOTOS;
  const canDelete = (p) => isAdmin || p.uploaded_by_id === user?.id;

  const add = async (kind, files) => {
    const room = MAX_PHOTOS - photos.length;
    if (files.length > room) toast.error(`Only ${room} more photo${room === 1 ? "" : "s"} allowed (max ${MAX_PHOTOS})`);
    setBusy(kind);
    let added = [...photos];
    for (const f of files.slice(0, room)) {
      try {
        const fd = new FormData();
        fd.append("kind", kind);
        fd.append("file", await compressImage(f));
        const { data } = await api.post(`/jobs/${job.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        added = [...added, data];
        setPhotos(added);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      }
    }
    setBusy("");
    onChange?.(added);
  };

  const remove = async (p) => {
    if (!window.confirm("Remove this photo?")) return;
    try {
      await api.delete(`/jobs/${job.id}/photos/${p.id}`);
      const next = photos.filter((x) => x.id !== p.id);
      setPhotos(next);
      onChange?.(next);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const group = (kind) => (
    <PhotoGroup jobId={job.id} kind={kind} photos={photos.filter((p) => p.kind === kind)} canAdd={canAdd}
      canDelete={canDelete} onAdd={add} onDelete={remove} onOpen={setOpen} busy={busy === kind} highlight={highlight === kind} />
  );

  return (
    <div className="space-y-5" data-testid="job-photos">
      {group("before")}
      {group("after")}
      <p className="text-xs text-muted-foreground">{photos.length}/{MAX_PHOTOS} photos · compressed automatically</p>
      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-3xl p-2" data-testid="photo-lightbox">
          <DialogTitle className="sr-only">Photo</DialogTitle>
          <DialogDescription className="sr-only">Full-size job photo</DialogDescription>
          {open && (
            <>
              <img src={photoUrl(job.id, open.id)} alt="Job" className="w-full max-h-[80vh] object-contain rounded-sm" />
              <div className="text-xs text-muted-foreground px-2 pb-1 capitalize">{open.kind} · {open.uploaded_by}</div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
